/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'

import type { EuiCheckboxGroupProps } from '@elastic/eui'
import { EuiCallOut, EuiCheckboxGroup, EuiSpacer } from '@elastic/eui'

import type {
  ElasticsearchClusterPlan,
  ElasticsearchUserPlugin,
  Extension,
} from '@modules/cloud-api/v1/types'
import { CuiLink } from '@modules/cui/Link'

import { satisfies } from '@/lib/semver'
import { getCustomPluginsFromPlan } from '@/lib/plugins'
import { deploymentExtensionsUrl } from '@/lib/urlBuilder'
import { getConfigForKey } from '@/store'

export type Props = {
  plan: ElasticsearchClusterPlan
  extensions: Extension[]
  selectedPluginUrls: string[]
  togglePlugin: (id: string) => void
  unsafeBundles: ElasticsearchUserPlugin[]
}

export default class VerifyPlugins extends Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { plan, togglePlugin, selectedPluginUrls, extensions } = this.props

    const { plan: prevPlan } = prevProps

    const version = plan.elasticsearch.version
    const prevVersion = prevPlan.elasticsearch.version

    if (version !== prevVersion) {
      selectedPluginUrls.forEach((url) => {
        if (!extensions.find((extension) => extension.url === url)) {
          togglePlugin(url)
        }
      })
    }
  }

  render() {
    const { unsafeBundles } = this.props

    if (unsafeBundles.length === 0) {
      return null
    }

    const { selectedPluginUrls, togglePlugin } = this.props

    const isUserConsole = getConfigForKey(`APP_NAME`) === `userconsole`

    const options = this.getOptions()
    const pluginIdToSelectedMap: EuiCheckboxGroupProps['idToSelectedMap'] = {}

    selectedPluginUrls.forEach((pluginId) => {
      pluginIdToSelectedMap[pluginId] = true
    })

    return (
      <EuiCallOut
        data-test-id='verify-plugins-call-out'
        color='warning'
        title={
          <FormattedMessage id='verify-plugins.upgrade-warning' defaultMessage='Upgrade warning' />
        }
      >
        <FormattedMessage
          id='verify-plugins.removed-plugins'
          defaultMessage='Upgrading this deployment will result in the following incompatible custom plugins being removed:'
        />

        <ul>
          {unsafeBundles.map((bundle) => (
            <li key={bundle.url}>
              {bundle.name} ({bundle.elasticsearch_version})
            </li>
          ))}
        </ul>

        {isUserConsole && (
          <FormattedMessage
            id='verify-plugins.add-plugin-user-console'
            defaultMessage='To add compatible plugins, go to <link>custom plugins</link>.'
            values={{
              link: (content: JSX.Element[]) => (
                <CuiLink to={deploymentExtensionsUrl()}>{content}</CuiLink>
              ),
            }}
          />
        )}

        {options.length > 0 && (
          <Fragment>
            <EuiSpacer size='s' />

            <FormattedMessage
              id='verify-plugins.compatible-plugins'
              defaultMessage='The following custom plugins are compatible with your new deployment version. Select the ones you would like to add.'
            />

            <EuiSpacer size='s' />

            <EuiCheckboxGroup
              data-test-id='verify-plugins-list'
              className='pluginCheckboxes'
              options={options}
              onChange={(url) => togglePlugin(url)}
              idToSelectedMap={pluginIdToSelectedMap}
            />
          </Fragment>
        )}
      </EuiCallOut>
    )
  }

  getOptions = () => {
    const { plan, extensions } = this.props

    const version = plan.elasticsearch.version!

    const planExtensions = getCustomPluginsFromPlan(plan)

    const existingPluginUrls = planExtensions.map((plugin) => plugin.url)

    return extensions
      .filter((extension) => satisfies(version, extension.version!))
      .filter((extension) => existingPluginUrls.indexOf(extension.url) === -1)
      .map((extension) => ({
        id: extension.url,
        label: `${extension.name} (${extension.version!})`,
      }))
  }
}
